import { html, nothing } from "lit-html";
import {
  ResultInformation,
  CategoryInformation,
  OpeningHoursInformation,
  CurrentDayOpeningInformation,
  MetaInformation,
} from "Interfaces/locationFilter";
import FRALocationFilter from "Components/fra-m-location-filter/fra-m-location-filter";
import { renderLazyImage } from "Components/fra-e-lazy-image/fra-e-lazy-image.template"; // eslint-disable-line
import { ACTIVE } from "Helper/cssClasses";

const alphabet = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
  "0-9", // will be also used as the regex to match locations
];

/**
 * Renders halls in enabled/disabled state
 * @param {Array<Object>} halls
 * @param {Function} setState
 * @returns {TemplateResult}
 */
export const locationFilterHallsTemplate = (
  halls: Array<{ name: string; selected: boolean; disabled: boolean }>,
  setState: Function,
) => {
  return halls.map(
    hall =>
      html`
        <button
          ?disabled=${hall.disabled}
          class="fra-m-location-filter__filter fra-m-location-filter__filter--hall ${hall.selected ? ACTIVE : ""}"
          @click=${() => {
            window.dataLayer?.push({
              event: "gaEvent",
              eventCategory: "locationFilter",
              eventAction: "Hall switch",
              eventLabel: `Hall ${hall.selected ? "" : hall.name}`,
            });
            setState({ selectedHall: hall.selected ? "" : hall.name });
          }}
        >
          ${hall.name}
        </button>
      `,
  );
};

/**
 * Renders single category filter option in enabled/disabled state
 * @param {CategoryInformation} category
 * @param {string | undefined} activeCategory
 * @param {FRALocationFilter} component
 * @returns {TemplateResult}
 */
export const renderSingleCategory = (
  category: CategoryInformation,
  activeCategory: string | undefined,
  component: FRALocationFilter,
) => {
  const activeClass = category.id === activeCategory ? "fra-state-active" : "";

  return html`
    <a
      class="fra-e-tag fra-m-location-filter__filter-category ${activeClass}"
      @click=${(e: Event) => component.handleCategoryFilterClick(e)}
    >
      <span class="fra-m-location-filter__category-label">${category.label}</span>
      <fra-e-icon class="fra-e-icon fra-m-location-filter__category-close-btn" icon-id="fra-icon-close"></fra-e-icon>
    </a>
  `;
};

/**
 * Renders category filters in enabled/disabled state
 * @param {Array<CategoryInformation>} categories
 * @param {Function} setState
 * @returns {TemplateResult}
 */
export const locationFilterCategoriesTemplate = (
  categories: Array<CategoryInformation & { selected: boolean }>,
  setState: Function,
) => {
  return categories.map(
    category => html`
      <button
        class="fra-e-tag fra-m-location-filter__filter-category ${category.selected ? ACTIVE : ""}"
        @click=${() => {
          window.dataLayer?.push({
            event: "gaEvent",
            eventCategory: "locationFilter",
            eventAction: "Category selection",
            eventLabel: `Category id ${category.selected ? "" : category.id}`,
          });
          setState({ selectedCategory: category.selected ? "" : category.id });
        }}
      >
        <span class="fra-m-location-filter__category-label">${category.label}</span>
        <fra-e-icon class="fra-e-icon fra-m-location-filter__category-close-btn" icon-id="fra-icon-close"></fra-e-icon>
      </button>
    `,
  );
};

/**
 * Renders single alphabet entry in enabled/disabled state
 * @param {string} entry
 * @param {string} disabledClass
 * @param {FRALocationFilter} component
 * @returns {TemplateResult}
 */
const renderSingleAlphabetEntry = (entry: string, disabledClass: string, component: FRALocationFilter) => {
  return html`
    <li class="fra-m-location-filter__alphabet-item">
      <a
        href="#_${entry}"
        class="fra-m-location-filter__alphabet-link ${disabledClass}"
        @click=${(e: Event) => component.handleAlphabetAnchorsClick(e)}
      >
        <span>${entry}</span>
      </a>
    </li>
  `;
};

/**
 * Renders alphabet row
 * @param {Array<ResultInformation>} results
 * @param {FRALocationFilter} component
 * @returns {TemplateResult}
 */
export const locationFilterAlphabetTemplate = (results: Array<ResultInformation>, component: FRALocationFilter) => {
  return html`
    <ul class="fra-m-location-filter__alphabet-list">
      ${alphabet.map(entry => {
        // locations starting with this letter
        const filteredResults = results.filter(result => new RegExp(`^[${entry}]`, "i").test(result.title));
        const disabledClass = filteredResults.length === 0 ? "fra-state-disabled" : "";
        return renderSingleAlphabetEntry(entry, disabledClass, component);
      })}
    </ul>
  `;
};

/**
 * Renders opening status of single result
 * @param {OpeningHoursInformation} openingArgs
 * @param {CurrentDayOpeningInformation} currentDayOpeningArgs
 * @param {MetaInformation} metaInformation
 * @returns {TemplateResult}
 */
const renderResultOpeningStatus = (
  openingArgs: OpeningHoursInformation,
  currentDayOpeningArgs: CurrentDayOpeningInformation,
  metaInformation: MetaInformation,
) => {
  let cssClass = "fra-m-location-filter__result-opening-hours--closed";
  let label = metaInformation.closeLabel;
  const now = Date.now();

  // if now is between openingTimestamp and closingTimestamp:
  if (now > currentDayOpeningArgs.openingTimestamp && now < currentDayOpeningArgs.closingTimestamp) {
    const differenceInMinutes = (currentDayOpeningArgs.closingTimestamp - now) / 1000 / 60;
    if (differenceInMinutes < 60) {
      cssClass = "fra-m-location-filter__result-opening-hours--closes-soon";
      label = metaInformation.closesSoonLabel;
    } else {
      cssClass = "fra-m-location-filter__result-opening-hours--open";
      label = metaInformation.openLabel;
    }
  }

  return html`
    <p class="fra-m-location-filter__result-opening-hours ${cssClass}">
      ${label}
      <span class="fra-m-location-filter__result-closing-time">• ${openingArgs.closingTime}</span>
    </p>
  `;
};

/**
 * Renders single result
 * @param {ResultInformation} result
 * @param {MetaInformation} metaInformation
 * @returns {TemplateResult}
 */
const renderSingleResult = (result: ResultInformation, metaInformation: MetaInformation) => {
  return html`
    <div class="fra-m-location-filter__result">
      <div class="fra-m-location-filter__result-image">
        ${result.logo === null
          ? nothing
          : renderLazyImage({
              src: result.logo.logoUrl,
              alt: result.image.altText,
              srcset: "",
              sizes: "",
              aspectRatio: "1:1",
            })}
      </div>
      <div class="fra-m-location-filter__result-data">
        <a
          class="fra-m-location-filter__result-link fra-e-link fra-e-link--with-icon fra-e-link--animated-right"
          href="${result.url}"
          @click=${() => {
            window.dataLayer?.push({
              event: "gaEvent",
              eventCategory: "locationFilter",
              eventAction: "Location click",
              eventLabel: `Location ${result.title}`,
            });
          }}
          target="_blank"
        >
          <span class="fra-e-link__label">${result.title}</span>
          <fra-e-icon class="fra-e-icon" icon-id="fra-icon-arrow-right"></fra-e-icon>
        </a>
        <p class="fra-m-location-filter__result-area">
          ${result.zone.label.includes("the_squaire")
            ? `The Squaire, ${result.zone.label.split(",")[2]}`
            : result.zone.label}
        </p>
        ${result.currentDayOpening && result.openingHours?.length
          ? renderResultOpeningStatus(result.openingHours[0], result.currentDayOpening, metaInformation)
          : nothing}
      </div>
    </div>
  `;
};

/**
 * Renders result section
 * @param {string} entry
 * @param {Array<ResultInformation>} results
 * @param {MetaInformation} metaInformation
 * @returns {TemplateResult}
 */
const renderSingleAlphabetSection = (
  entry: string,
  results: Array<ResultInformation>,
  metaInformation: MetaInformation,
) => {
  return html`
    <div class="fra-m-location-filter__result-section">
      <div class="fra-m-location-filter__result-section-identifier" id="_${entry}">${entry}</div>
      <div class="fra-m-location-filter__results">
        ${results.map(result => renderSingleResult(result, metaInformation))}
      </div>
    </div>
  `;
};

/**
 * Renders results
 * @param {Array<ResultInformation>} results
 * @param {MetaInformation} metaInformation
 * @returns {TemplateResult}
 */
export const locationFilterResultsTemplate = (results: Array<ResultInformation>, metaInformation: MetaInformation) => {
  if (!results) return nothing;

  return html`
    ${alphabet.map(letter => {
      // locations starting with this letter
      const filteredResults = results
        .filter(result => new RegExp(`^[${letter}]`, "i").test(result.title))
        .sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()));
      if (filteredResults.length === 0) return nothing;
      return renderSingleAlphabetSection(letter, filteredResults, metaInformation);
    })}
  `;
};
